// @ts-nocheck
import { Flag, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AI_AGENT_FIELD_LIMITS } from '@/utilities/constant';
import { getAiAgentDescriptionCharLimit } from '@/utilities/aiAgentAcl';
import { c, Section, Field, Empty, IconBtn, useStudio, useNode, useShown } from '../ui';

const { COMPLETION_TYPE_MAX_ITEMS: MAX, COMPLETION_TYPE_NAME_MAX: NAME_MAX } = AI_AGENT_FIELD_LIMITS;
const SNAKE = /^[a-z0-9_]+$/;

export default function CompletionSection({ next, onNext, value, onChange, handover }) {
  const list = value || [];
  const { touch, data, readOnly, goTo } = useStudio();
  const node = useNode('completion');
  const shown = useShown();
  const descLimit = getAiAgentDescriptionCharLimit();

  const errors = (r) => {
    const e = {};
    const t = (r.type || '').trim();
    if (!t) e.type = 'Add a short type, like booking_confirmed.';
    else if (!SNAKE.test(t)) e.type = 'Use lowercase letters, numbers and underscores only, like booking_confirmed.';
    else if (list.some((o) => o.id !== r.id && (o.type || '').trim() === t)) e.type = 'This type is already used. Each type must be unique.';
    if (!r.description?.trim()) e.description = 'Describe when this happens so the AI can recognise it.';
    return e;
  };

  const add = (seed = {}) => {
    if (list.length >= MAX) {
      toast.error(`You can add up to ${MAX} completion types`);
      return;
    }
    onChange([...list, { id: crypto.randomUUID(), type: '', description: '', ...seed }]);
  };
  const update = (id, patch) => onChange(list.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const changeType = (id, raw) => {
    const spaced = raw.toLowerCase().replace(/\s+/g, '_');
    const cleaned = spaced.replace(/[^a-z0-9_]/g, '');
    if (cleaned !== spaced) toast.error('Type of completion must be snake_case (lowercase letters, numbers and underscores only)', { id: 'cr-type-chars' });
    update(id, { type: cleaned.slice(0, NAME_MAX) });
  };

  const hasHumanRule = list.some((r) => r.type === 'caller_requests_human');
  const filled = list.filter((r) => r.type?.trim() || r.description?.trim());
  const defaults = (data?.defaultCompletions?.items || []).filter((d) => d.name);

  const usingDefaults = !handover || filled.length === 0;

 
  const useDefault = (d) => {
    if (list.some((r) => (r.type || '').trim() === d.name)) {
      toast.error('This type is already in your rules');
      return;
    }
    const blank = list.find((r) => !r.type?.trim() && !r.description?.trim());
    if (blank) update(blank.id, { type: d.name, description: d.description });
    else add({ type: d.name, description: d.description });
  };

  return (
    <Section
      id="completion"
      icon={Flag}
      title="Completion rules"
      sub={handover ? 'Optional. Tell the agent when a call counts as finished.' : 'Works together with Human handover.'}
      tag={!handover ? 'optional' : filled.length ? (node.bad ? 'required' : 'done') : 'optional'}
      ok={node.ok}
      bad={node.bad}
      next={next('completion')}
      onNext={onNext}
    >
      {!handover && (
        <div className={c('note')} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <span>
            Turn on Human handover to add your own completion rules.
            {filled.length > 0 ? ' Your rules are kept but are not used while it is off.' : ''}
          </span>
          {!readOnly && (
            <button type="button" className={c('btn small')} onClick={() => goTo('handover')}>
              Go to Human handover
            </button>
          )}
        </div>
      )}
      {handover && (
        <div className={c('cnt')} style={{ margin: '0 0 10px' }}>
          <span>
            {list.length} of {MAX} rules
          </span>
        </div>
      )}
      {handover && list.map((r, i) => {
        const e = errors(r);
        const blank = !r.type?.trim() && !r.description?.trim();
        const show = (shown('completion') || shown(`completion.${r.id}`)) && (!blank || shown('completion') && filled.length === 0);
        return (
          <div className={c('form-card')} key={r.id}>
            <div className={c('fh')}>
              <Flag className="ags-i" /> Rule {i + 1}
              <span style={{ marginLeft: 'auto' }}>
                <IconBtn icon={Trash2} danger label={`Delete rule ${i + 1}`} onClick={() => onChange(list.filter((x) => x.id !== r.id))} />
              </span>
            </div>
            <Field label="Type of completion" required note="A short identifier in snake_case. Example: caller_requests_human." error={show && e.type}>
              <input
                className={c('ctl', show && e.type && 'invalid')}
                value={r.type}
                maxLength={NAME_MAX}
                placeholder="e.g. card_selected"
                onChange={(ev) => changeType(r.id, ev.target.value)}
                onBlur={() => touch(`completion.${r.id}`)}
              />
            </Field>
            <Field label="Description" required style={{ margin: 0 }} count={(r.description || '').length} max={descLimit} note="" error={show && e.description}>
              <textarea
                className={c('ctl', show && e.description && 'invalid')}
                rows={2}
                value={r.description}
                maxLength={descLimit}
                placeholder="e.g. Customer confirmed which card they want and agreed to close the chat."
                onChange={(ev) => update(r.id, { description: ev.target.value.slice(0, descLimit) })}
                onBlur={() => touch(`completion.${r.id}`)}
              />
            </Field>
          </div>
        );
      })}
      {defaults.length > 0 && (
        <div className={c('form-card')}>
          <div className={c('fh')}>
            <Flag className="ags-i" /> Default completion types
          </div>
          <p className={c('note')} style={{ margin: '0 0 10px' }}>
            {!handover
              ? 'These are used to build the flow chart. They are not saved to the agent.'
              : filled.length === 0
                ? 'You have not added your own rules, so these are used to build the flow chart. They are not saved to the agent.'
                : 'Not used, because you added your own rules. Clear your rules to go back to these.'}
          </p>
          {defaults.map((d) => (
            <div className={c('item', !usingDefaults && 'off')} key={d.id ?? d.name}>
              <span className={c('cb')}>
                <Flag className="ags-i" />
              </span>
              <div style={{ minWidth: 0 }}>
                <b style={{ overflowWrap: 'anywhere' }}>{d.name}</b>
                {d.description && <p style={{ overflowWrap: 'anywhere' }}>{d.description}</p>}
              </div>
              {!readOnly && handover && (
                <div className={c('acts')}>
                  <button type="button" className={c('btn small')} onClick={() => useDefault(d)}>
                    <Plus className="ags-i" />
                    Add as rule
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {handover && list.length === 0 && defaults.length === 0 && (
        <Empty icon={Flag} title="No completion rules yet" text="Optional. Add one so the agent knows when a call is done." />
      )}
      {handover && (
      <div className={c('addrow')}>
        <button type="button" className={c('btn small')} onClick={() => add()} disabled={list.length >= MAX}>
          <Plus className="ags-i" />
          Add rule
        </button>
        {!hasHumanRule && (
          <button type="button" className={c('sg')} onClick={() => add({ type: 'caller_requests_human', description: 'Caller asks to speak to a person.' })}>
            <Plus className="ags-i" style={{ width: 14, height: 14 }} /> Add caller_requests_human
          </button>
        )}
      </div>
      )}
    </Section>
  );
}
